<template>
  <div class="nurse-calls-history">
    <!-- Header Section -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-6 text-white mb-6">
        <div class="flex items-center">
          <div class="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
            <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold mb-1">{{ $t('nurse.callHistory') }}</h1>
            <p class="text-purple-100 text-sm">{{ $t('nurse.allVideoCallsWithPatients') }}</p>
          </div>
        </div>
      </div>
      
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">{{ $t('nurse.totalCalls') }}</p>
              <p class="text-2xl font-bold text-gray-900">{{ calls.length }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">{{ $t('common.today') }}</p>
              <p class="text-2xl font-bold text-gray-900">{{ getTodayCallsCount() }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-600">{{ $t('nurse.thisWeek') }}</p>
              <p class="text-2xl font-bold text-gray-900">{{ getWeekCallsCount() }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Calls History Section -->
    <div class="bg-white shadow-sm rounded-xl p-6 border border-gray-200">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-gray-800">{{ $t('nurse.callHistory') }}</h2>
        
        <div class="flex items-center space-x-4">
          <!-- Search bar -->
          <div class="relative">
            <input 
              v-model="searchQuery"
              type="text" 
              :placeholder="$t('nurse.searchByPatientOrIin')"
              class="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 text-sm w-64"
            >
            <svg class="w-4 h-4 text-gray-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          
          <!-- Period filter -->
          <select v-model="selectedPeriod" class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500">
            <option value="all">{{ $t('nurse.allTime') }}</option>
            <option value="today">{{ $t('common.today') }}</option>
            <option value="week">{{ $t('nurse.thisWeek') }}</option>
            <option value="month">{{ $t('nurse.thisMonth') }}</option>
          </select>
        </div>
      </div>
      
      <div v-if="loading" class="flex justify-center p-6">
        <HeartbeatLoader :text="$t('nurse.loadingCallHistory')" />
      </div>
      
      <div v-else-if="error" class="p-4 bg-red-50 text-red-600 rounded-md">
        {{ error }}
        <button @click="loadCallsHistory" class="ml-4 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm">
          {{ $t('messages.tryAgain') }}
        </button>
      </div>
      
      <div v-else>
        <div v-if="filteredCalls.length > 0" class="overflow-x-auto">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="bg-gray-50 text-gray-700">
                <th class="py-3 px-4 text-left font-medium">{{ $t('common.patient') }}</th>
                <th class="py-3 px-4 text-left font-medium">{{ $t('common.date') }}</th>
                <th class="py-3 px-4 text-left font-medium">{{ $t('common.time') }}</th>
                <th class="py-3 px-4 text-left font-medium">{{ $t('time.duration') }}</th>
                <th class="py-3 px-4 text-left font-medium">{{ $t('nurse.room') }}</th>
                <th class="py-3 px-4 text-left font-medium">{{ $t('common.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="call in filteredCalls" :key="call.id" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td class="py-3 px-4">
                  <div>
                    <div class="text-gray-900 font-medium">{{ call.patient_name }}</div>
                    <div class="text-gray-500 text-sm">{{ $t('common.iin') }}: {{ call.patient_iin }}</div>
                  </div>
                </td>
                <td class="py-3 px-4 text-gray-600">{{ formatDateOnly(call.created_at) }}</td>
                <td class="py-3 px-4 text-gray-600">{{ formatTimeOnly(call.created_at) }}</td>
                <td class="py-3 px-4 text-gray-600">
                  <span v-if="call.duration_minutes" class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {{ formatDuration(call.duration_minutes) }}
                  </span>
                  <span v-else class="text-gray-400 text-sm">{{ $t('common.notSpecified') }}</span>
                </td>
                <td class="py-3 px-4 text-gray-600">{{ call.room_name }}</td>
                <td class="py-3 px-4">
                  <div class="flex space-x-2">
                    <button 
                      @click="viewRecords(call)"
                      class="px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                      :disabled="loadingRecords[call.id]"
                    >
                      <span v-if="loadingRecords[call.id]">{{ $t('common.loading') }}</span>
                      <span v-else>{{ $t('appointment.meetingRecords') }}</span>
                    </button>
                    <button 
                      @click="openVideoRoom(call)"
                      class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      {{ $t('nurse.room') }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div v-else class="text-center p-8 text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
          </svg>
          <p v-if="searchQuery">{{ $t('nurse.callsNotFoundByQuery', { query: searchQuery }) }}</p>
          <p v-else-if="selectedPeriod !== 'all'">{{ $t('nurse.noCallsForPeriod') }}</p>
          <p v-else>{{ $t('nurse.noCallHistoryYet') }}</p>
        </div>
      </div>
    </div>

    <!-- Room Records Modal -->
    <div v-if="showRecordsModal" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- Backdrop overlay -->
      <div class="fixed inset-0 bg-black opacity-50" @click="closeRecordsModal"></div>
      
      <!-- Modal dialog -->
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto z-10">
          <div class="px-6 py-4 border-b">
            <h2 class="text-xl font-semibold text-gray-800">Записи видеозвонка</h2>
            <p v-if="selectedCall" class="text-sm text-gray-600 mt-1">
              Комната: {{ selectedCall.room_name }}
            </p>
          </div>
          
          <div class="p-6">
            <!-- Video Player Section -->
            <div v-if="selectedVideoUrl && showVideoPlayer" class="mb-6">
              <div class="bg-black rounded-lg overflow-hidden">
                <video 
                  :src="selectedVideoUrl" 
                  controls 
                  class="w-full h-64 md:h-96"
                  controlsList="nodownload"
                  @loadedmetadata="onVideoLoaded"
                >
                  Ваш браузер не поддерживает воспроизведение видео.
                </video>
              </div>
              <div class="flex justify-between items-center mt-3">
                <div class="text-sm text-gray-600">
                  <svg class="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                  </svg>
                  Воспроизведение записи звонка
                </div>
                <button 
                  @click="closeVideoPlayer"
                  class="text-gray-500 hover:text-gray-700 text-sm"
                >
                  Закрыть плеер
                </button>
              </div>
            </div>
            
            <div v-if="loadingRecords[selectedCall?.id]" class="flex justify-center p-6">
              <HeartbeatLoader />
              <span class="ml-2">{{ $t('patientDetails.records.loading') }}</span>
            </div>
            
            <div v-else-if="recordsError" class="text-center p-8">
              <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Ошибка загрузки</h3>
              <p class="text-red-600 mb-4">{{ recordsError }}</p>
              <button 
                @click="viewRecords(selectedCall)"
                class="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Попробовать снова
              </button>
            </div>
            
            <div v-else-if="selectedCallRecords.length > 0">
              <div class="flex items-center justify-between mb-4">
                <p class="text-gray-700">
                  Найдено записей: {{ selectedCallRecords.length }}
                </p>
                <div class="text-sm text-gray-500">
                  <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Нажмите "Просмотреть" для воспроизведения
                </div>
              </div>
              
              <div class="grid grid-cols-1 gap-3">
                <div 
                  v-for="(record, index) in selectedCallRecords" 
                  :key="`record-${index}`" 
                  class="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center">
                      <div class="w-12 h-12 bg-gradient-to-br from-purple-100 to-green-100 rounded-lg flex items-center justify-center mr-4">
                        <svg class="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                        </svg>
                      </div>
                      <div>
                        <h4 class="font-medium text-gray-900">Запись звонка {{ index + 1 }}</h4>
                        <p class="text-sm text-gray-500">Видеофайл в формате MP4</p>
                        <div class="flex items-center mt-1 text-xs text-gray-400">
                          <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                          </svg>
                          Готово к просмотру
                        </div>
                      </div>
                    </div>
                    
                    <div class="flex space-x-2">
                      <button 
                        @click="playVideo(record)"
                        class="px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center"
                      >
                        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                        </svg>
                        Просмотреть
                      </button>
                      <a 
                        :href="record"
                        target="_blank"
                        class="px-4 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
                      >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                        </svg>
                        Открыть
                      </a>
                      <a 
                        :href="record"
                        :download="`nurse-call-record-${index + 1}.mp4`"
                        class="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
                      >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        Скачать
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else class="text-center p-8">
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Записей не найдено</h3>
              <p class="text-gray-600">Для этого звонка пока нет доступных записей.</p>
            </div>
          </div>
          
          <div class="px-6 py-3 border-t bg-gray-50 flex justify-end rounded-b-lg">
            <button 
              @click="closeRecordsModal"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'
import HeartbeatLoader from '../components/common/HeartbeatLoader.vue'

export default {
  name: 'NurseCallsHistory',
  components: {
    HeartbeatLoader
  },
  data() {
    return {
      calls: [],
      loading: true,
      error: null,
      searchQuery: '',
      selectedPeriod: 'all',
      showRecordsModal: false,
      selectedCall: null,
      selectedCallRecords: [],
      loadingRecords: {},
      recordsError: null,
      // Video player state
      selectedVideoUrl: null,
      showVideoPlayer: false
    }
  },
  computed: {
    filteredCalls() {
      let filtered = this.calls

      // Фильтр по поиску
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(call => 
          call.patient_name.toLowerCase().includes(query) ||
          call.patient_iin.includes(query)
        )
      }

      // Фильтр по периоду
      if (this.selectedPeriod !== 'all') {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        
        filtered = filtered.filter(call => {
          const callDate = new Date(call.created_at)
          
          switch (this.selectedPeriod) {
            case 'today':
              return callDate >= today
            case 'week': {
              const weekAgo = new Date(today)
              weekAgo.setDate(weekAgo.getDate() - 7)
              return callDate >= weekAgo
            }
            case 'month': {
              const monthAgo = new Date(today)
              monthAgo.setMonth(monthAgo.getMonth() - 1)
              return callDate >= monthAgo
            }
            default:
              return true
          }
        })
      }

      return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }
  },
  async mounted() {
    await this.loadCallsHistory()
  },
  methods: {
    async loadCallsHistory() {
      try {
        this.loading = true
        this.error = null
        
        const response = await api.getNurseVideoCallsHistory()
        this.calls = response.data || []
        
      } catch (error) {
        this.error = 'Не удалось загрузить историю звонков'
      } finally {
        this.loading = false
      }
    },
    
    async viewRecords(call) {
      try {
        if (!call.room_name) {
          alert('Название комнаты недоступно для этого звонка.');
          return;
        }

        this.selectedCall = call;
        this.showRecordsModal = true;
        this.loadingRecords[call.id] = true;
        this.selectedCallRecords = [];
        this.recordsError = null;

        
        const response = await api.getRoomRecords(call.room_name);
        
        
        if (response.data && response.data.result === 'success' && response.data.data) {
          this.selectedCallRecords = response.data.data.record_urls || [];
          
          if (this.selectedCallRecords.length > 0) {
          } else {
      // No action needed
          }
        } else if (response.data && response.data.result === 'error') {
          this.recordsError = response.data.error || 'Ошибка получения записей';
          this.selectedCallRecords = [];
        } else {
          this.selectedCallRecords = [];
        }
      } catch (error) {
        this.recordsError = 'Не удалось загрузить записи звонка. Возможно, записи еще не готовы.';
        this.selectedCallRecords = [];
      } finally {
        this.loadingRecords[call.id] = false;
      }
    },
    
    openVideoRoom(call) {
      window.open(call.room_url, '_blank')
    },
    
    closeRecordsModal() {
      this.showRecordsModal = false
      this.selectedCall = null
      this.selectedCallRecords = []
      this.recordsError = null
      this.closeVideoPlayer()
    },
    
    formatDuration(minutes) {
      if (!minutes) return 'Неизвестно'
      
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      
      if (hours > 0) {
        return `${hours}ч ${mins}м`
      } else {
        return `${mins}м`
      }
    },
    
    formatDateOnly(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU')
    },
    
    formatTimeOnly(dateString) {
      const date = new Date(dateString)
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    },
    
    getTodayCallsCount() {
      const today = new Date()
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      
      return this.calls.filter(call => {
        const callDate = new Date(call.created_at)
        return callDate >= todayStart
      }).length
    },
    
    getWeekCallsCount() {
      const today = new Date()
      const weekAgo = new Date(today)
      weekAgo.setDate(weekAgo.getDate() - 7)
      
      return this.calls.filter(call => {
        const callDate = new Date(call.created_at)
        return callDate >= weekAgo
      }).length
    },
    
    playVideo(record) {
      this.selectedVideoUrl = record;
      this.showVideoPlayer = true;
    },
    
    closeVideoPlayer() {
      this.selectedVideoUrl = null;
      this.showVideoPlayer = false;
    },
    
    onVideoLoaded() {
      // Handle video loaded metadata
    }
  }
}
</script>

<style scoped>
.nurse-calls-history {
  /* All styles are handled by Tailwind CSS */
}
</style> 